import { FormArray, FormControl, FormControlOptions, FormGroup } from '@angular/forms';
import { ApiTypes } from '../../api/api-types';
import { ApiEnums } from '../../api/generated/api-enums';
import { IAdvertisement } from '../../common/interfaces/advertisement.interface';
import { IBranding } from '../../common/interfaces/branding.interface';
import { ISurveyTranslation } from '../../components/survey/interfaces/survey-translation.interface';

export const QuestionType = ApiEnums.QuestionType;
export type QuestionType = ApiEnums.QuestionType;

/** Converts the contract's question type literal into the enum the forms switch on. */
export const QUESTION_TYPES: Record<ApiTypes.QuestionType, QuestionType> = {
  boolean: QuestionType.Boolean,
  checkbox: QuestionType.Checkbox,
  date: QuestionType.Date,
  image_multiple_select: QuestionType.ImageMultipleSelect,
  image_single_select: QuestionType.ImageSingleSelect,
  radio: QuestionType.Radio,
  scale: QuestionType.Scale,
  text: QuestionType.Text,
};

export const IMAGE_QUESTION_TYPES: ReadonlySet<QuestionType> = new Set([
  QuestionType.ImageSingleSelect,
  QuestionType.ImageMultipleSelect,
]);

export type TranslationGroup = Partial<Record<'campaignQuestion' | 'option', ISurveyTranslation[]>>;

// A type alias, not an interface: FormGroup's control map needs the implicit index signature.
export type QuestionFormControls = {
  options?: FormArray<QuestionOptionFormControl>;
  text?: FormControl<string>;
  answer?: FormControl<string>;
};

export class QuestionFormGroup extends FormGroup<QuestionFormControls> {
  constructor(
    readonly questionUuid: string,
    readonly type: QuestionType,
    controls: QuestionFormControls
  ) {
    super(controls);
  }
}

export interface IQuestionOption {
  uuid: string;
  isOther?: boolean;
  translations?: ISurveyTranslation[] | TranslationGroup;
}
export interface IQuestion {
  uuid: string;
  type: QuestionType;
  options?: IQuestionOption[] | null;
  translations?: ISurveyTranslation[] | TranslationGroup;
}
export interface IQuestionTranslation {
  questionText: string;
  optionsTexts: string[];
}
export type IQuestionPayloadAnswer = Exclude<
  NonNullable<ApiTypes.QuestionAnswerInput['answer']>,
  string
>[number];
export interface IQuestionPayload extends ApiTypes.QuestionAnswerInput {
  token?: string;
  visitorId?: string;
}
export interface IGeneralVenueInfo extends Pick<
  ApiTypes.CampaignTakeawayInfoResponseDto,
  'venueName' | 'languages'
> {
  advertisement?: IAdvertisement | null;
  branding: IBranding | null;
}
// FormControl's constructor overloads return differently typed controls, which a subclass cannot
// extend (TS2510); this picks the non-nullable one.
const NonNullableBooleanControl: new (
  value: boolean,
  options: FormControlOptions & { nonNullable: true }
) => FormControl<boolean> = FormControl;

export class QuestionOptionFormControl extends NonNullableBooleanControl {
  constructor(
    private readonly optionUuid: string,
    private readonly isOther: boolean,
    isSelected = false
  ) {
    super(isSelected, { nonNullable: true });
  }

  getOptionUuid(): string {
    return this.optionUuid;
  }

  isOtherOption(): boolean {
    return this.isOther;
  }
}
